import type {
  CollateralType, DeliveryType, RateEntryMode, TermType, TradeStatus, TradeType,
} from '@/types/trades.types';
import { poolsItsCash } from '@/utils/trades/cash-pool-rate';

/**
 * The lifecycle actions hosted on the TradesPage row action panel (and the
 * prototype trade-detail action bar). Each maps 1:1 to an existing
 * PositionViewSet @action endpoint (see frontend/src/hooks/trades).
 * `retry` is LR-408 (re-queue a BO-rejected position).
 */
export type ActionId =
  | 'give_notice'
  | 'set_settlement_date'
  | 'settle'
  | 'settle_cash'
  | 'settle_securities'
  | 'retry'
  | 'partial_recall'
  | 'terminate'
  // LR-478: amend the booked quantity before the trade opens (PATCH).
  | 'amend'
  // LR-544: manually fire the Trade Date action ("Open now") on a pending
  // position instead of waiting for the hourly cron.
  | 'process_trade_date'
  // LR-528: re-rate an open trade — change the rate leg from an effective date
  // (POST /positions/{id}/re-rate/). active + backdated matured.
  | 're_rate'
  // Give a cash pool to a trade booked before one was mandatory
  // (POST /positions/{id}/assign-cash-pool/).
  | 'assign_cash_pool';

/**
 * Structural subset of the fields the validity predicate reads. Typed as a
 * standalone interface (NOT one concrete model) so BOTH callers fit without a
 * cast: `PositionDetail` (detail page) and `TradeItem` (TradesPage) each carry
 * these fields. Do not import either concrete type as the predicate param.
 */
/**
 * LR-576 (feedback п.70): shared hover copy disambiguating the close-date
 * scheduler from Terminate. Single source — the FAB, the STV toolbar and the
 * blotter row-chips must never drift apart on these sentences.
 */
export const SET_CLOSE_DATE_TOOLTIP =
  'Set or change the close settlement date — the status does not change, and an active trade still closes only once it has matured';
/**
 * LR-695: Terminate fixes the day the loan ends (the recall / return of GMSLA
 * §8), not the day the status flips. The date becomes the maturity date and the
 * close date at once; the trade stays active and accrues until then.
 */
export const TERMINATE_TOOLTIP =
  'Terminate the trade: fixes the date the loan ends — it stays active and accrues until then, matures on that date and closes the same day (today ends it now)';
export const CANCEL_REJECTED_TOOLTIP =
  'Cancel the rejected position — it never opened in BO';
/**
 * LR-678: shown where Give Notice would otherwise be offered on an evergreen
 * that carries no notice period. Such a trade cannot serve notice at all — the
 * backend derives the maturity date from the period — so the action is withheld
 * rather than failing at Confirm.
 */
export const GIVE_NOTICE_NO_PERIOD_TOOLTIP =
  'Notice period is not set for this trade — notice cannot be served';

/**
 * The leg `settle_cash` posts is the collateral leg: cash for cash collateral, the
 * collateral securities for non-cash — so there the action and its dialog say "Collateral".
 */
export const collateralLegName = (collateralType?: string | null): 'Cash' | 'Collateral' =>
  collateralType === 'non_cash' ? 'Collateral' : 'Cash';

export interface TradeActionFields {
  status: TradeStatus;
  term_type?: TermType;
  notice_served?: boolean;
  // LR-678: Give Notice needs it to derive the maturity date. Nullable on legacy
  // rows booked before the form defaulted it, so the predicate must read it.
  notice_period_days?: number | null;
  // LR-695: Set Close Date on an active trade needs an end date to close on —
  // booked (Term), derived by Give Notice (Evergreen) or fixed by Terminate.
  // Null on an Open trade until it is terminated; the list fixtures use ''.
  maturity_date?: string | null;
  delivery_type?: DeliveryType;
  cash_settled?: boolean;
  securities_settled?: boolean;
  // LR-388: partial recall/return validity reads the trade side (lend → recall,
  // borrow → return) and the markable shape left (0 ⇒ nothing more to recall).
  trade_type?: TradeType;
  remaining_quantity?: number;
  collateral_type?: CollateralType;
  rate_entry_mode?: RateEntryMode;
  cash_pool?: number | null;
}

/**
 * Terminal lifecycle states. They expose NO lifecycle actions, and no live
 * Mark-to-Market either — a closed trade has no live mark (a future
 * "View MTM History" action will serve those positions instead).
 * LR-408: `rejected` is intentionally NOT terminal here — it still exposes Retry
 * + Cancel (see getValidActions). It has no live mark, so the panel hides
 * Mark-to-Market for it separately.
 */
export function isTerminalStatus(status: TradeStatus): boolean {
  return status === 'closed' || status === 'cancelled';
}

/**
 * Pure `status -> valid-actions` predicate. The SINGLE source of truth for both
 * the render path (which buttons appear in the row action panel) and the submit
 * path (whether a mutation is allowed to fire), mirroring the v1.1
 * `isMtmMarkable` lesson: "disabling an input is not the same as blocking the
 * action." The submit handler re-checks this against the selected trade, so a
 * stale row, keyboard, or race cannot fire an action invalid for the current
 * lifecycle state. Backend prerequisites are the ultimate authority (defense in
 * depth); this guards the FE submit path.
 *
 * Validity rules (binding "Action validity by lifecycle state" matrix):
 *  - give_notice:        status === 'active' AND term_type === 'evergreen' AND
 *                        notice_served !== true AND notice_period_days != null
 *                        AND no maturity_date
 *                        (LR-678: without the period the backend cannot derive
 *                        the maturity date and rejects at Confirm;
 *                        LR-695 r2: with an end date already fixed — by
 *                        Terminate, or a legacy preset — notice would move it
 *                        later; the end is moved with Terminate instead)
 *                        (LR-677: a Matured evergreen has
 *                        already reached its close date by definition — notice
 *                        cannot retroactively initiate a closure that already
 *                        happened; it closes via set_settlement_date like any
 *                        other Matured position)
 *  - set_settlement_date: status === 'matured', OR status === 'active' with a
 *                        maturity_date (LR-412: the close-side date is
 *                        meaningless before the trade opens; LR-695: on an
 *                        active trade it is inert until the end of the trade is
 *                        fixed — a close date on an Open trade before Terminate
 *                        closed nothing, ever)
 *  - settle:             status === 'pending_open_settlement'
 *  - settle_cash:        status === 'pending_open_settlement' AND
 *                        delivery_type === 'fop' AND cash_settled !== true
 *  - settle_securities:  status === 'pending_open_settlement' AND
 *                        delivery_type === 'fop' AND securities_settled !== true
 *  - terminate:          status === 'active' (LR-695: fixes the day the loan
 *                        ends — maturity and close date together; the trade
 *                        stays active until then, matured is refused by the
 *                        backend — from there the close date moves via
 *                        set_settlement_date)
 *  - retry / Cancel:     status === 'rejected' (LR-408) — exclusive of all other
 *                        actions; never opened, so only re-queue or cancel.
 *  - amend:              status === 'pending' (LR-478) — amend booked quantity
 *                        before the BO hold; rate/spread is a separate ticket.
 *
 * Pure: no React, no I/O, no toast/store/mutation references. Importable by the
 * panel and tests. Kept byte-aligned with prototype-single-trade-page's
 * trade-actions.ts (same path/exports) for trivial convergence on merge.
 */
export function getValidActions(trade: TradeActionFields): Set<ActionId> {
  const actions = new Set<ActionId>();
  const {
    status, term_type, notice_served, notice_period_days, maturity_date, delivery_type,
    cash_settled, securities_settled, trade_type, remaining_quantity,
  } = trade;

  // Terminal states expose zero actions (binding matrix: closed/cancelled rows
  // are entirely empty). Short-circuit so no per-action rule can re-admit one.
  if (isTerminalStatus(status)) {
    return actions;
  }

  // LR-408: a BO-rejected position never opened in BO. Its only actions are
  // Retry (reset → pending for the next worker run) and Cancel (the terminate
  // endpoint, date-less). No give_notice / settle / set_settlement_date apply,
  // and the panel hides live Mark-to-Market for it separately.
  if (status === 'rejected') {
    actions.add('retry');
    actions.add('terminate');
    return actions;
  }

  // give_notice: evergreen term that has not yet served notice, and ONLY while
  // still active (LR-677). Matured already has its close date fixed — by
  // whatever means it got there — so notice has nothing left to initiate; the
  // pre-LR-684 preset-maturity-date bug produced Matured evergreens with
  // notice_served still false, and offering Give Notice there would fabricate
  // an audit record (notice_served_by/_date) for a closure no one actually
  // initiated. Those close via set_settlement_date like any other Matured
  // position. (LR-436 D5: also gated off pending / pending_open_settlement —
  // a recall/return notice presupposes an open position to recall — folded
  // into status === 'active' here since neither pending state is active.)
  // LR-678: and only with a notice period on the trade. `Position.give_notice`
  // derives the maturity date from it and 400s without it — pre-LR-678 the panel
  // offered the action, the dialog opened, and the trade died at Confirm. The
  // field is nullable for evergreens booked before the form defaulted it (all
  // terminal today), and there is no supported way to set it after booking, so
  // the action is withheld rather than disabled-with-a-remedy.
  // LR-695 r2: and only while the trade has no end date yet. Terminate on an
  // un-noticed evergreen now fixes the end (maturity_date) and leaves the row
  // active; notice on that row would overwrite the end with notice date + period
  // — later than the agreed return whenever the period outruns what is left,
  // the very extension the terminate endpoint refuses. The backend guard in
  // Position.give_notice is the authority; this keeps the button honest. An end
  // already fixed is moved with Terminate (set_settlement_date stays offered).
  if (
    status === 'active' &&
    term_type === 'evergreen' &&
    notice_served !== true &&
    notice_period_days != null &&
    !maturity_date
  ) {
    actions.add('give_notice');
  }

  // LR-478: amend the booked quantity ONLY while still pending — before the BO
  // hold is placed. Once it moves to pending_open_settlement the
  // terms are committed to settlement. The backend (PositionUpdateSerializer)
  // re-validates this same gate.
  // LR-544: a pending position can also be opened manually ("Open now") — fire
  // the Trade Date action instead of waiting for the hourly cron. This gate is
  // status-only; the future-trade-date case is handled at the button (disabled
  // with a tooltip) and re-validated by the backend.
  if (status === 'pending') {
    actions.add('amend');
    actions.add('process_trade_date');
  }

  // set_settlement_date: this is the CLOSE-side settlement date. It only makes
  // sense once the trade has actually opened in BO (active / matured). LR-412:
  // pre-open states (pending, pending_open_settlement) have no open position to
  // close yet, so the close date is meaningless there.
  // LR-695: and on an ACTIVE trade only once the end of the trade is fixed.
  // Closure runs on matured rows, and only the maturity run produces matured
  // without an operator — so with no maturity date a close date closed nothing,
  // ever (the LR-695 stuck rows: Open trades with a close date years past, still
  // active). The end date comes from booking (Term), Give Notice (Evergreen) or
  // Terminate (any) — until then the action is withheld rather than warned
  // about. Matured keeps it unconditionally: there the date IS the settlement.
  // Truthiness, not `!= null`: the list fixtures carry '' for "no maturity".
  // (The FE is intentionally stricter than the model's set_settlement_date, which
  // accepts any non-terminal status — this gate is a product choice, not a
  // backend constraint.)
  if (status === 'matured' || (status === 'active' && !!maturity_date)) {
    actions.add('set_settlement_date');
  }

  // settle and its FOP-only legs: only while awaiting open settlement.
  if (status === 'pending_open_settlement') {
    actions.add('settle');

    if (delivery_type === 'fop') {
      // Per-leg settle for FOP trades, suppressing an already-settled leg.
      if (cash_settled !== true) {
        actions.add('settle_cash');
      }
      if (securities_settled !== true) {
        actions.add('settle_securities');
      }
    }
  }

  // partial_recall / return (LR-388): an active or matured stock lend/borrow can have part
  // of its shape recalled (lend) or returned (borrow), as long as there is still a markable
  // remaining quantity — a fully-recalled position (remaining 0) has nothing left to recall.
  // Repo/reverse are excluded (not repriced via MTM — LR-385). The backend re-validates.
  if (
    (status === 'active' || status === 'matured') &&
    (trade_type === 'lend' || trade_type === 'borrow') &&
    remaining_quantity !== 0
  ) {
    actions.add('partial_recall');
  }

  // re_rate (LR-528): change the rate leg of an OPEN trade from an effective
  // date. `active` accepts T0 / future / backdated (within the current month);
  // `matured` accepts a BACKDATED change only (the backend reprices the accrued
  // window, LR-527). Pre-open and terminal states have no live rate to re-rate.
  // The backend PositionReRateSerializer re-validates status (409) + date (400).
  if (status === 'active' || status === 'matured') {
    actions.add('re_rate');
  }

  // assign_cash_pool: a cash trade with no pool, of a shape a booking would pool. Strictly
  // `null`, not "absent": a payload without the field says nothing about the pool. A trade
  // with pool movements but no pool is the backend's to refuse.
  if (
    (status === 'active' || status === 'matured') &&
    trade.collateral_type === 'cash' &&
    trade.cash_pool === null &&
    poolsItsCash(trade_type, trade.rate_entry_mode)
  ) {
    actions.add('assign_cash_pool');
  }

  // terminate: only an ACTIVE position can be terminated. The backend's
  // terminate endpoint moves active → matured (scheduling closure); for pre-open
  // states it does not progress the lifecycle (it merely stamps a close-settlement
  // date — misleading), and matured/closed/cancelled are rejected outright.
  // Mirrors the pre-LR-104 UI (canTerminate = status === 'active').
  if (status === 'active') {
    actions.add('terminate');
  }

  return actions;
}

/**
 * Whether a single action may be performed on the given trade. Defined as
 * delegation to `getValidActions` so render and submit can NEVER disagree —
 * there is exactly one rule body (anti-drift). Do not duplicate the rules here.
 */
export function canPerform(trade: TradeActionFields, actionId: ActionId): boolean {
  return getValidActions(trade).has(actionId);
}
